import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Loader2, ArrowLeft } from "lucide-react";
import { login } from "../../services/auth";
import Stepper, { Step } from "../../components/signO/ui/Stepper";
import LightPillar from "../../components/signO/ui/LightPillar";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      await login(formData.email, formData.password);
      navigate("/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <LightPillar
          topColor="#5227FF"
          bottomColor="#FF9FFC"
          intensity={1.0}
          rotationSpeed={0.3}
          glowAmount={0.005}
          pillarWidth={3.0}
          pillarHeight={0.4}
          noiseIntensity={0.5}
          pillarRotation={0}
          interactive={false}
          mixBlendMode="normal"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card w-full max-w-md p-8 relative z-10 my-8"
      >
        <Link
          to="/"
          className="absolute top-4 left-4 text-gray-400 hover:text-white transition-colors z-20"
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>

        <div className="text-center mb-6 mt-4">
          <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
          <p className="text-gray-400">Sign in to continue to SignO</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-3 rounded-lg mb-6 text-sm text-center">
            {error}
          </div>
        )}

        <Stepper
          initialStep={1}
          onFinalStepCompleted={handleLogin}
          backButtonText="Back"
          nextButtonText="Next"
          stepCircleContainerClassName="bg-transparent shadow-none border-none"
          stepContainerClassName="mb-8"
          contentClassName="min-h-[150px] flex flex-col justify-center"
          footerClassName="mt-4 flex justify-end items-center gap-4"
        >
          <Step>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4 text-center">
                Enter your email
              </h3>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                  placeholder="you@example.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      // Prevent default form submission if any
                      e.preventDefault();
                      // Logic to go to next step is handled by Stepper's Next button,
                      // but we can't programmatically trigger it easily without ref.
                      // For now, user clicks Next.
                    }
                  }}
                />
              </div>
            </div>
          </Step>
          <Step>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4 text-center">
                Enter your password
              </h3>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-dark/50 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder-gray-500"
                  placeholder="••••••••"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleLogin();
                    }
                  }}
                />
              </div>
            </div>
          </Step>
        </Stepper>

        {loading && (
          <div className="absolute inset-0 bg-dark/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-2xl">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        )}

        <p className="mt-8 text-center text-gray-400 text-sm relative z-20 pb-2">
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="text-primary hover:text-primary/80 font-medium"
          >
            Sign up
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
